import type { HealthAction } from "@safers/api";

import { SEVERITY_PALETTE, type SeverityKey } from "@safers/ui";

export type HeatLevel = "SAFE" | "INTEREST" | "CAUTION" | "WARNING" | "DANGER";

export interface HeatLevelMeta {
  level: HeatLevel;
  label: string;
  /** 200 */
  chipBg: string;
  /** 800 */
  chipFg: string;
  /** 50 */
  panelBg: string;
  /** 700 */
  panelFg: string;
  /** 500 — 글자 없는 채움 */
  fill: string;
}

/** heatLevelFromSeverity 의 역방향 */
const HEAT_TO_SEVERITY: Record<HeatLevel, SeverityKey> = {
  SAFE: "NORMAL",
  INTEREST: "ATTENTION",
  CAUTION: "CAUTION",
  WARNING: "WARNING",
  DANGER: "CRITICAL",
};

const HEAT_LABELS: Record<HeatLevel, string> = {
  SAFE: "정상",
  INTEREST: "관심",
  CAUTION: "주의",
  WARNING: "경고",
  DANGER: "위험",
};

export const HEAT_LEVEL_META: Record<HeatLevel, HeatLevelMeta> = Object.fromEntries(
  (Object.keys(HEAT_TO_SEVERITY) as HeatLevel[]).map((level) => {
    const p = SEVERITY_PALETTE[HEAT_TO_SEVERITY[level]];
    return [
      level,
      {
        level,
        label: HEAT_LABELS[level],
        chipBg: p.chipBg,
        chipFg: p.chipFg,
        panelBg: p.panelBg,
        panelFg: p.panelFg,
        fill: p.fill,
      },
    ];
  })
) as Record<HeatLevel, HeatLevelMeta>;

export function heatLevelOf(heatIndex: number): HeatLevel {
  if (heatIndex >= 38) return "DANGER";
  if (heatIndex >= 35) return "WARNING";
  if (heatIndex >= 33) return "CAUTION";
  if (heatIndex >= 31) return "INTEREST";
  return "SAFE";
}

export function heatLevelFromSeverity(severity: string | null | undefined): HeatLevel {
  switch (severity) {
    case "CRITICAL":
      return "DANGER";
    case "WARNING":
      return "WARNING";
    case "CAUTION":
      return "CAUTION";
    case "ATTENTION":
      return "INTEREST";
    default:
      return "SAFE";
  }
}

/**
 * 등급 판정 규칙은 여기 한 곳에만 둔다.
 * 판정에 쓰는 두 필드만 요구해, point 가 없는 자리(레포트의 체감온도 배지)에서도 부를 수 있다.
 */
export function heatLevelOfPoint(p: { heatIndex: number; severity?: string | null }): HeatLevel {
  return p.severity != null ? heatLevelFromSeverity(p.severity) : heatLevelOf(p.heatIndex);
}

export interface HealthSensor {
  id: number;
  name: string;
}

export type ActionKind = "REST" | "OUTDOOR_STOP" | "WORK_END" | "CUSTOM";

export const ACTION_KINDS: { value: ActionKind; label: string }[] = [
  { value: "REST", label: "휴식" },
  { value: "OUTDOOR_STOP", label: "옥외작업 중단" },
  { value: "WORK_END", label: "작업종료" },
  { value: "CUSTOM", label: "직접입력" },
];

export const REST_MINUTES = [10, 15, 20, 25, 30];
export const DEFAULT_REST_MINUTES = 20;

export function composeActionType(kind: ActionKind, restMinutes: number, custom: string): string {
  switch (kind) {
    case "REST":
      return `휴식-${restMinutes}분`;
    case "OUTDOOR_STOP":
      return "옥외작업 중단";
    case "WORK_END":
      return "작업종료";
    case "CUSTOM":
      return custom.trim();
  }
}

const REST_TEXT = /^휴식[-\s](\d+)분$/;

export function parseActionType(actionType: string): {
  kind: ActionKind;
  restMinutes: number;
  custom: string;
} {
  const rest = REST_TEXT.exec(actionType);
  if (rest) return { kind: "REST", restMinutes: Number(rest[1]), custom: "" };
  if (actionType === "옥외작업 중단")
    return { kind: "OUTDOOR_STOP", restMinutes: DEFAULT_REST_MINUTES, custom: "" };
  if (actionType === "작업종료")
    return { kind: "WORK_END", restMinutes: DEFAULT_REST_MINUTES, custom: "" };
  return { kind: "CUSTOM", restMinutes: DEFAULT_REST_MINUTES, custom: actionType };
}

export function actionTypeParts(actionType: string): { label: string; detail: string } {
  const { kind, custom } = parseActionType(actionType);
  if (kind === "REST") return { label: actionType, detail: "" };
  const label = ACTION_KINDS.find((k) => k.value === kind)?.label ?? actionType;
  if (kind === "CUSTOM") return { label: "기타", detail: custom };
  return { label, detail: "" };
}

export interface HealthActionInput {
  actionType: string;
  worker: string;
  deviceId?: number | null;
  temperature: number;
  humidity: number;
  heatIndex: number;
  measuredTime: string;
  author?: string;
  actionAt: string;
  newFiles: File[];
  keepFileIds: number[];
}

export const HEALTH_PHOTO_MAX = 5;

export type ReportRow = { label: string } & Record<string, number | string>;

export interface DailyReport {
  siteName: string;
  date: string;
  sensorCount: number;
  seriesNames: string[];
  hourly: ReportRow[];
  actions: HealthAction[];
}

export interface MonthlyDailyPoint {
  date: string;
  maxHeatIndex: number;
  avgHeatIndex: number;
  actionCount: number;
}

/**
 * 센서 하나의 등급별 알림 수.
 *
 * **월간 레포트에는 기상청이 들어가지 않는다** — 현장에 설치된 센서만 센다(백엔드 의도).
 * 스펙 설명에는 "맨 앞은 항상 기상청" 이라고 적혀 있으나 실제 응답에는 없다. 순서를
 * 가정하지 말고 받은 대로 쓴다.
 */
export interface SensorAlarmRow {
  name: string;
  count: number;
  /** 등급별 건수. 서버 6단을 보건 5단으로 접어 합산한 결과 */
  byLevel: { level: HeatLevel; label: string; count: number }[];
}

export interface MonthlyReport {
  siteName: string;
  yearMonth: string;
  daily: MonthlyDailyPoint[];
  levelCounts: { level: HeatLevel; label: string; count: number }[];
  totalActions: number;
  /** 월 전체 온열 알림 수. 화면이 '알림 없음' 을 한 번만 말하도록 미리 센다 */
  totalAlarms: number;
  sensorAlarms: SensorAlarmRow[];
}
